import axios from 'axios';
import { validateEnv } from '../../utils/env.util';
import { getDeviceId } from '../../utils/device-id.util';
import { employeeSessionFingerprint, logEmployeeSessionAudit } from '../employee-session-audit/employee-session-audit.service';

const { VITE_BACKEND_LOCATION } = validateEnv();
export const EMPLOYEE_SESSION_EXPIRED_EVENT = 'pos:employee-session-expired';
export const EMPLOYEE_SESSION_LOOP_PROBE_EVENT = 'pos:employee-session-loop-probe';

type FailedResponseShape = {
    statusCode?: number;
    message?: string;
};

const API_ERROR_TO_HEBREW: Record<string, string> = {
    'Invalid or expired employee session': 'פג תוקף ההתחברות של העובד. יש להתחבר מחדש.',
    'Employee session is not valid anymore': 'התחברות העובד כבר לא תקפה. יש להתחבר מחדש.',
    'Employee session was replaced by another device': 'העובד התחבר ממכשיר אחר. יש לבחור עובד להתחברות מחדש.',
    'Employee session ended with the shift': 'המשמרת נסגרה. יש לבחור עובד להתחברות מחדש.',
    'Table is currently being used by another device': 'לא ניתן לבצע פעולה בזמן שמשתמש אחר עובד בשולחן.',
    'x-employee-session header is required': 'חסר אישור התחברות עובד. יש להתחבר מחדש.',
    'Invalid employee pin': 'קוד שגוי, אנא נסה שנית או פנה למנהל לאיפוס הקוד',
    'Select employee': 'יש לבחור עובד.',
    'Failed loading employees': 'טעינת העובדים נכשלה.',
    'Failed loading areas': 'טעינת האזורים נכשלה.',
    'Failed loading active session': 'טעינת המשמרת הפעילה נכשלה.',
    'Failed loading employee settings': 'טעינת הגדרות העובדים נכשלה.',
    'Failed loading area settings': 'טעינת הגדרות האזורים נכשלה.',
    'Failed loading printers': 'טעינת המדפסות נכשלה.',
    'Failed loading print jobs': 'טעינת עבודות ההדפסה נכשלה.',
    'Failed loading products': 'טעינת המוצרים נכשלה.',
    'No active session to close': 'אין משמרת פעילה לסגירה.',
    'No open session for tenant. Open a session first.': 'הקופה סגורה. יש לפתוח משמרת כדי להמשיך.',
    'Failed opening session': 'פתיחת משמרת נכשלה.',
    'Failed closing session': 'סגירת משמרת נכשלה.',
    'Failed opening order': 'פתיחת הזמנה נכשלה.',
    'ORDER_CODE_CONFLICT': 'נוצרה התנגשות בעת פתיחת ההזמנה. נסו שוב.',
    'Failed closing order': 'סגירת הזמנה נכשלה.',
    'No items have been sent to the kitchen yet. Send items before payment.': 'עדיין לא נשלחו פריטים למטבח. שלחו פריטים לפני התשלום.',
    'Failed loading orders': 'טעינת ההזמנות נכשלה.',
    'Failed creating test print': 'יצירת הדפסת בדיקה נכשלה.',
    'Failed updating print job status': 'עדכון סטטוס עבודת הדפסה נכשל.',
    'No active cash-drawer printer is configured for this tenant': 'לא הוגדרה מדפסת עם מגירת קופה. סמנו "מגירת קופה" בסוכן המדפסת.',
    'Multiple cash-drawer printers are configured. Choose a specific printer.': 'יש יותר ממדפסת מגירה אחת. בחרו מדפסת מגירה ספציפית.',
    'Cash-drawer printer not found for this tenant': 'מדפסת מגירת הקופה לא נמצאה.',
    'Only managers can apply discounts': 'רק מנהלים יכולים להחיל הנחות.',
    'Only managers can cancel finalized receipt items': 'רק מנהלים יכולים לבטל פריטים שנשלחו.',
    'PENDING_APPROVAL_EXISTS': 'קיימת בקשת מנהל פתוחה להזמנה, יש להמתין לאישור או דחייה',
    'Cannot cancel items beyond the unpaid balance': 'סכום הפריט גדול מסכום היתרה לתשלום.',
    'Full order cannot be canceled because a paid paymentline exists': 'קיימת שורת תשלום משולמת',
    'Order not found or already closed': 'ההזמנה לא נמצאה או כבר נסגרה.',
    'Orderline not found or order not open': 'פריט ההזמנה לא נמצא או ההזמנה אינה פתוחה.',
    'Selected items are no longer available for item payment': 'הפריטים שנבחרו כבר לא זמינים לפיצול לפי פריטים.',
    'Selected items must leave a remaining balance to split': 'יש לבחור רק חלק מהפריטים כדי להשאיר יתרה לפיצול.',
    'Selected session must be closed before viewing transactions': 'ניתן לצפות בעסקאות רק עבור משמרת סגורה.',
    'No active Z-Credit terminal is configured for this tenant': 'לא הוגדר מסוף Z-Credit פעיל עבור העסק.',
    'OWNER_PUSH_NOT_CONFIGURED': 'אין למשתמש הבעלים התראת push פעילה. יש להפעיל התראות PWA במכשיר הבעלים או לכבות את ההגדרה.',
    'NO_PUSH_DEVICE_REGISTERED': 'אין מכשיר רשום להתראות בעסק. יש להתקין את האפליקציה ולהירשם להתראות, או לכבות את ההגדרה.',
    'Acting employee not allowed': 'בחירת המלצר אינה זמינה. יש לבחור מלצר מחדש או לפנות למנהל.',
    'Only managers can close orders with cash': 'רק מנהל יכול לסגור הזמנה במזומן.',
};

// Summary: Maps backend/client errors to concise Hebrew UI-safe messages.
// Details: docs/function-library/web/services.md#index-service
export const toHebrewApiErrorMessage = (message: unknown): string => {
    const normalized = typeof message === 'string' ? message.trim() : '';
    if (!normalized) {
        return 'אירעה שגיאה. נסו שוב.';
    }

    const mapped = API_ERROR_TO_HEBREW[normalized];
    if (mapped) {
        return mapped;
    }

    if (/[֐-׿]/.test(normalized)) {
        return normalized;
    }

    return 'אירעה שגיאה. נסו שוב.';
};

// Summary: Detects employee-session-expired API responses for global session recovery flows.
// Details: docs/function-library/web/services.md#index-service
export const isEmployeeSessionExpiredResponse = (payload: unknown): payload is FailedResponseShape => {
    if (!payload || typeof payload !== 'object') {
        return false;
    }

    const asResponse = payload as FailedResponseShape;
    return (
        asResponse.statusCode === 401
        && (
            asResponse.message === 'Invalid or expired employee session'
            || asResponse.message === 'Employee session was replaced by another device'
            || asResponse.message === 'Employee session ended with the shift'
        )
    );
};

export const authenticatedInstance  = axios.create({
    baseURL: VITE_BACKEND_LOCATION,
    withCredentials: true,
});

// בחירת מלצר בעמדה נייחת: while set, every POS write is attributed to this employee (backend-validated).
let actingEmployeeId: number | null = null;
export const setActingEmployeeId = (id: number | null): void => {
    actingEmployeeId = id;
};

// Tag every request with the persistent device id so backend-captured error rows get מכשיר too.
authenticatedInstance.interceptors.request.use((config) => {
    try {
        config.headers.set('x-device-id', getDeviceId());
    } catch {
        // Header tagging is diagnostic-only; never block a request on it.
    }
    if (actingEmployeeId !== null && !config.headers.has('x-acting-employee-id')) {
        config.headers.set('x-acting-employee-id', String(actingEmployeeId));
    }
    return config;
});

// True when a 401 came from a request carrying an employee-session token that is no longer the
// tenant's current stored session. Such a token belongs to a superseded/stale in-flight request
// (e.g. a lingering SSE reconnect after this device already took its session over); acting on it
// would wrongly log the device out of its still-valid current session and force a self-takeover loop.
export const isSupersededEmployeeSessionRequest = (requestUrl: string, requestToken: unknown): boolean => {
    if (typeof requestToken !== 'string' || requestToken.length === 0) {
        return false;
    }
    const tenantMatch = /\/tenant\/([^/?]+)/.exec(requestUrl);
    const tenantId = tenantMatch?.[1] ? decodeURIComponent(tenantMatch[1]) : null;
    if (!tenantId) {
        return false;
    }
    try {
        const raw = localStorage.getItem(`posEmployeeSession:${tenantId}`);
        if (!raw) {
            return false;
        }
        const stored = JSON.parse(raw) as { employeeSession?: unknown };
        return typeof stored.employeeSession === 'string'
            && stored.employeeSession.length > 0
            && stored.employeeSession !== requestToken;
    } catch {
        return false;
    }
};

authenticatedInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        const responseData = error?.response?.data;
        const requestHeaders = error?.config?.headers as Record<string, unknown> | undefined;
        const requestUrl = typeof error?.config?.url === 'string' ? error.config.url : '';
        const isTablePresenceRequest = /\/pos\/tenant\/[^/]+\/table\/\d+\/presence(?:\/|$)/.test(requestUrl);
        const usedEmployeeSessionHeader = Boolean(
            requestHeaders?.['x-employee-session'] ?? requestHeaders?.['X-Employee-Session'],
        );
        const requestEmployeeSession = requestHeaders?.['x-employee-session'] ?? requestHeaders?.['X-Employee-Session'];
        const isRecognizedSessionExpiry = isEmployeeSessionExpiredResponse(responseData);
        const isSupersededRequest = isSupersededEmployeeSessionRequest(requestUrl, requestEmployeeSession);

        if (
            usedEmployeeSessionHeader
            && !isTablePresenceRequest
            && !isSupersededRequest
            && isRecognizedSessionExpiry
            && typeof window !== 'undefined'
        ) {
            logEmployeeSessionAudit('response_401_picker_reset_dispatched', {
                requestUrl,
                responseMessage: responseData.message,
                requestSession: employeeSessionFingerprint(typeof requestEmployeeSession === 'string' ? requestEmployeeSession : null),
            });
            window.dispatchEvent(new CustomEvent(EMPLOYEE_SESSION_EXPIRED_EVENT, {
                detail: {
                    reason: 'employee-session-401',
                    statusCode: responseData.statusCode,
                    message: responseData.message,
                    requestUrl,
                },
            }));
        } else if (usedEmployeeSessionHeader && typeof window !== 'undefined') {
            if (isSupersededRequest && isRecognizedSessionExpiry) {
                logEmployeeSessionAudit('response_superseded_session_loop_probe_dispatched', {
                    requestUrl,
                    statusCode: responseData.statusCode,
                    responseMessage: responseData.message,
                    requestSession: employeeSessionFingerprint(typeof requestEmployeeSession === 'string' ? requestEmployeeSession : null),
                });
                window.dispatchEvent(new CustomEvent(EMPLOYEE_SESSION_LOOP_PROBE_EVENT, {
                    detail: {
                        reason: 'superseded-session-token',
                        statusCode: responseData.statusCode,
                        message: responseData.message,
                        requestUrl,
                    },
                }));
            }
            logEmployeeSessionAudit('response_with_employee_session_ignored', {
                requestUrl,
                statusCode: responseData?.statusCode ?? error?.response?.status ?? null,
                responseMessage: responseData?.message ?? null,
                reason: isTablePresenceRequest
                    ? 'table-presence-excluded'
                    : isSupersededRequest
                        ? 'superseded-session-token'
                        : isRecognizedSessionExpiry ? 'window-unavailable' : 'not-recognized-session-expiry',
                requestSession: employeeSessionFingerprint(typeof requestEmployeeSession === 'string' ? requestEmployeeSession : null),
            });
        }

        return Promise.reject(error);
    },
);
